import { pageTitle } from '@palaccount/config';
import { BANKING_MESSAGES, type CheckResponse } from '@palaccount/contracts';
import { AlertTriangle, Flag, Plus, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { usePagedState, usePageRange } from '@/lib/paging';
import { localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { useBaseCurrency, useCompanyToday, useDocumentDate } from '@/lib/session';
import { FxPanel } from '@/features/documents/fx-panel';

import {
  useBankAccounts,
  useBouncedFlagHistory,
  useChecks,
  useChecksAwaitingAction,
  useCreateCheck,
  useReissueCheck,
  useReverseCheck,
  useSetBouncedFlag,
  useTransitionCheck,
  useVoidCheck,
} from './api';
import { BankingTabs } from './banking-tabs';
import { canReissue, canReverse, canVoid, dueChip, isDeadRow, nextStatesOf } from './lifecycle';

const FLAG_WINDOWS = [3, 7, 14, 30] as const;

/**
 * FIN-10 Incoming Checks / FIN-11 Outgoing Checks — one component, two
 * directions, because "most of it behaves identically" (FIN-11 §1). The
 * reminders rail (cheques awaiting action, overdue first), the register on
 * the leading side, and the stacked detail panels: fields, the lifecycle
 * with ONLY legal next states offered, the bounce panel with its one-shot
 * Reverse, the outgoing-only void panel (Issued only, reason mandatory), and
 * owner ruling R10's bounced-flag control beside the customer's name —
 * offered only to holders of the override permission, refused server-side
 * for everyone else however it is called.
 */
export function ChecksPage({ direction }: { direction: 'incoming' | 'outgoing' }) {
  // N-16: the COMPANY's today, never the reader's machine clock.
  const today = useCompanyToday();
  const { t } = useTranslation();
  /** NFR-42 — the register pages; the rail is its own query, see below. */
  const paging = usePagedState();
  const { data, isPending } = useChecks(direction, paging.page, paging.pageSize);
  const checks = data?.checks;
  const range = usePageRange(data, paging.pageSize);
  const { data: awaiting } = useChecksAwaitingAction(direction);
  // ADM-04 §BR-ADM-04-06 — a notification about a cheque must OPEN THAT
  // CHEQUE, and FIN-10/FIN-11 are registers with a detail panel rather than a
  // per-cheque route. `?check=<id>` is that link's landing point; it seeds the
  // selection once and is then ordinary state, so clicking another row does
  // not fight the URL. It survives paging because the lookup below spans the
  // register page AND the awaiting-action set before falling back to the head.
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('check'));
  const [creating, setCreating] = useState(false);
  const [flagWindow, setFlagWindow] = useState(7);
  // GEN-107: what the company reports in. Read once for the whole register —
  // the rate line below is drawn only for cheques that are not in it.
  const baseCurrency = useBaseCurrency();

  const titleKey = direction === 'incoming' ? 'banking.incomingTitle' : 'banking.outgoingTitle';
  useEffect(() => {
    document.title = pageTitle(t(titleKey));
  }, [t, titleKey]);

  // A rail click can select a cheque that is not on the register's current
  // page, so the lookup spans both sets before falling back to the page head.
  const selected =
    (checks ?? []).find((check) => check.id === selectedId) ??
    (awaiting?.checks ?? []).find((check) => check.id === selectedId) ??
    (checks ?? [])[0];

  /*
   * BR-FIN-10-15: only cheques awaiting action, sorted overdue first.
   *
   * It reads `awaiting`, NOT the paged register: the awaiting-action set is
   * the server's own predicate now, because deriving the rail from page 1
   * would quietly stop reminding about an overdue cheque that sorted onto
   * page 2. The chip itself (overdue / due-soon / post-dated) is still
   * computed here — it depends on the company's today and on the reader's
   * flag-window control, neither of which belongs in a query.
   */
  const rail = useMemo(() => {
    const now = today;
    return (awaiting?.checks ?? [])
      .map((check) => ({ check, chip: dueChip(check, now, flagWindow) }))
      .filter(({ chip }) => chip.kind !== 'none')
      .sort((a, b) => a.chip.days - b.chip.days);
  }, [awaiting, flagWindow, today]);

  return (
    <div className="grid gap-4">
      <BankingTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t(titleKey)}</h2>
        <Label htmlFor="flag-window" className="text-xs text-muted-foreground">
          {t('banking.flagDueWithin')}
        </Label>
        <NativeSelect
          id="flag-window"
          className="h-9 w-auto text-sm"
          value={String(flagWindow)}
          onChange={(event) => setFlagWindow(Number(event.target.value))}
        >
          {FLAG_WINDOWS.map((days) => (
            <option key={days} value={days}>
              {t('banking.daysOption', { count: days })}
            </option>
          ))}
        </NativeSelect>
        <Gated permission="banking.create" reasonKey="perm.cannot.createChecks">
          <Button onClick={() => setCreating((value) => !value)}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>

      {/* The reminders rail. */}
      {rail.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {direction === 'incoming' ? t('banking.railEmptyIn') : t('banking.railEmptyOut')}
        </p>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {rail.map(({ check, chip }) => (
            <button
              key={check.id}
              type="button"
              onClick={() => setSelectedId(check.id)}
              className="min-w-44 shrink-0 rounded-lg border p-2 text-start text-xs hover:bg-muted/50"
            >
              <span className="flex items-center justify-between gap-2">
                <Ltr value={check.code} className="font-mono text-muted-foreground" />
                <span
                  className={`rounded-full px-2 py-0.5 font-medium ${
                    chip.kind === 'overdue'
                      ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                      : chip.kind === 'due_soon'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {chip.kind === 'overdue'
                    ? t('banking.overdueDays', { count: -chip.days })
                    : chip.kind === 'due_soon'
                      ? t('banking.dueInDays', { count: chip.days })
                      : t('banking.postDatedIn', { count: chip.days })}
                </span>
              </span>
              <span className="mt-1 block font-medium">
                {check.partnerName ?? check.payeeName ?? '—'}
              </span>
              <span className="mt-1 flex items-center justify-between gap-2">
                <Ltr value={check.chequeDate} className="font-mono text-muted-foreground" />
                <Amount value={check.amount} currency={check.currency} />
              </span>
            </button>
          ))}
        </div>
      )}

      {creating ? (
        <NewCheckForm direction={direction} onDone={() => setCreating(false)} />
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        {/* The register. */}
        <div className="grid content-start gap-2">
          {isPending ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p> : null}
          {(checks ?? []).map((check) => (
            <button
              key={check.id}
              type="button"
              data-testid={`check-row-${check.code}`}
              onClick={() => setSelectedId(check.id)}
              className={`rounded-lg border p-3 text-start ${
                check.id === selected?.id ? 'border-primary ring-2 ring-primary/30' : 'hover:bg-muted/50'
              } ${isDeadRow(check.status) ? 'opacity-55' : ''}`}
            >
              <span className="flex flex-wrap items-center justify-between gap-2 text-sm font-semibold">
                <span>{check.partnerName ?? check.payeeName ?? '—'}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                  {t(`banking.status.${check.status}`)}
                </span>
                {/*
                 * D31 — THE PENDING OBLIGATION, ON THE REGISTER.
                 *
                 * The ruling asks for exactly this: "hold the posting until the
                 * cheque date, WITH THE PENDING OBLIGATION VISIBLE ON THE
                 * CHEQUE REGISTER" — and decision 31's own reasoning says why
                 * it cannot be left off ("an unposted obligation exists in the
                 * meantime, which has to be visible somewhere").
                 *
                 * AMBER, and it is not a choice: NFR-14 pins the five meanings
                 * product-wide, and "amber = waiting for somebody or something"
                 * is what a held posting is — it is waiting for a date. Grey is
                 * taken ("not started, or a draft") and would say the wrong
                 * thing, because a held cheque is not a draft: the cheque
                 * exists, the obligation exists, only the entry is waiting. The
                 * tone is the exact class string the due-soon chip above
                 * already uses, so no colour value is introduced here.
                 *
                 * NFR-39/GEN-61: the colour is the redundant channel. The chip
                 * says the DATE in words, so a reader who sees no colour at all
                 * still learns when this cheque posts.
                 *
                 * It sits BESIDE the status chip rather than replacing it,
                 * because they answer different questions — `deposited` is
                 * where the cheque is, "Posts on the 30th" is where its entry
                 * is — and a row that showed only one of them would hide the
                 * other.
                 */}
                {check.postingDueOn !== null ? (
                  <span
                    data-testid={`check-posting-held-${check.code}`}
                    className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                  >
                    {t('banking.postingHeld', { date: check.postingDueOn })}
                  </span>
                ) : null}
              </span>
              <span className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                {/* BR-FIN-11-06: a dead number is retained, struck through. */}
                <Ltr
                  value={`${check.chequeNumber} · ${check.chequeDate}`}
                  className={`font-mono ${isDeadRow(check.status) ? 'line-through' : ''}`}
                />
                <Amount value={check.amount} currency={check.currency} />
              </span>
              {/*
               * D19's cheque door, finished on the screen it was missing from.
               * `checks.service.ts` recorded the gap in prose: each event's
               * rate was "defensible in the LEDGER and invisible on the cheque
               * register — where BR-SAL-04-09's reasoning would want it, since
               * a rate without its date can be repeated but not DEFENDED". So
               * the register prints the rate AND the date it came from.
               *
               * FOREIGN CHEQUES ONLY. GEN-116 and ADM-01: a single-currency
               * business "should never see the word currency", and a cheque in
               * the base has converted nothing — there is no panel, not an
               * empty one and not a rate of 1.
               *
               * A held cheque reads "Not frozen until posted" (fx.notFrozen)
               * for the same reason a draft document does, and it is the same
               * sentence: nothing has posted, so nothing has frozen. That is
               * the two halves of this wave agreeing on one row.
               */}
              {check.currency !== baseCurrency ? (
                <span
                  data-testid={`check-rate-${check.code}`}
                  className="mt-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground"
                >
                  <span>{t('fx.rate')}</span>
                  {check.exchangeRate === null ? (
                    <span>{t('fx.notFrozen')}</span>
                  ) : (
                    <Ltr
                      value={
                        check.rateEffectiveOn === null
                          ? `1 ${baseCurrency} = ${check.exchangeRate} ${check.currency}`
                          : `1 ${baseCurrency} = ${check.exchangeRate} ${check.currency} · ${check.rateEffectiveOn}`
                      }
                      className="font-mono"
                    />
                  )}
                </span>
              ) : null}
            </button>
          ))}
          {!isPending && (checks ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('banking.registerEmpty')}</p>
          ) : null}
          {/* NFR-42 — the range, the total, and the two steps. */}
          {!isPending && (data?.total ?? 0) > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t(titleKey)}
            />
          ) : null}
        </div>

        {selected ? <CheckDetail key={selected.id} check={selected} /> : null}
      </div>
    </div>
  );
}

function NewCheckForm({
  direction,
  onDone,
}: {
  direction: 'incoming' | 'outgoing';
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateCheck();

  const { data: bankAccounts } = useBankAccounts();
  const [chequeNumber, setChequeNumber] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [issuingBank, setIssuingBank] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [docDate, setDocDate] = useDocumentDate();
  const [chequeDate, setChequeDate] = useDocumentDate();
  const [error, setError] = useState<string | null>(null);

  /*
   * GEN-50 — a cheque names a customer (incoming) or a payee (outgoing), and
   * the side it is on is the SERVER's `type` filter rather than a fold over
   * every partner in the tenant.
   */
  const partyPicker = usePartnerPicker(partnerId || null, {
    type: direction === 'incoming' ? 'customer' : 'supplier',
  });
  // BR-FIN-11-01: drawn-on offers only bank and cash accounts.
  const drawable = (bankAccounts ?? []).filter(
    (account) => account.accountType === 'bank' || account.accountType === 'cash',
  );

  const submit = async () => {
    setError(null);
    try {
      await create.mutateAsync({
        direction,
        chequeNumber: chequeNumber.trim(),
        ...(partnerId === '' ? {} : { partnerId }),
        ...(direction === 'outgoing' && partnerId === '' && payeeName.trim() !== ''
          ? { payeeName: payeeName.trim() }
          : {}),
        ...(direction === 'incoming' && issuingBank.trim() !== ''
          ? { issuingBank: issuingBank.trim() }
          : {}),
        ...(direction === 'outgoing' ? { bankAccountId } : {}),
        amount,
        docDate,
        chequeDate,
      });
      onDone();
    } catch (cause) {
      // Second route, same as the bank-accounts screen: this never called
      // `localizeServerMessage`, so a refusal that arrived was shown in the
      // server's English and a request that never came back showed the
      // browser's `Failed to fetch`. One call answers both.
      setError(localizeServerCause(t, cause, 'banking.errSaveFailedCheques'));
    }
  };

  return (
    <Card>
      <CardContent className="grid gap-4">
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="grid gap-1.5">
            <Label htmlFor="c-number">{t('banking.fChequeNumber')}</Label>
            <Input
              id="c-number"
              dir="ltr"
              value={chequeNumber}
              onChange={(event) => setChequeNumber(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="c-partner">
              {direction === 'incoming' ? t('banking.fCustomer') : t('banking.fPayee')}
            </Label>
            {/* The cheque's side decides which record the create row makes:
                an incoming cheque names a customer, an outgoing one a payee.
                Same expression the picker's own server filter uses, so the
                row cannot offer to create something this field then refuses
                to show. */}
            <PartnerTypeahead
              entity={direction === 'incoming' ? 'customer' : 'supplier'}
              id="c-partner"
              label={direction === 'incoming' ? t('banking.fCustomer') : t('banking.fPayee')}
              value={partnerId || null}
              onChange={(id) => setPartnerId(id ?? '')}
              options={partyPicker.options}
              selected={partyPicker.optionFor(partnerId)}
              query={partyPicker.query}
              onQueryChange={partyPicker.setQuery}
              clearLabel={
                direction === 'incoming' ? t('common.choose') : t('banking.oneOffPayee')
              }
              emptyLabel={
                direction === 'incoming'
                  ? t('picker.noMatchingCustomer')
                  : t('picker.noMatchingSupplier')
              }
              loading={partyPicker.loading}
            />
          </div>
          {direction === 'outgoing' && partnerId === '' ? (
            <div className="grid gap-1.5">
              <Label htmlFor="c-payee">{t('banking.oneOffPayee')}</Label>
              {/* The dashed border — the product's unregistered-party signal. */}
              <Input
                id="c-payee"
                className="border-dashed"
                value={payeeName}
                onChange={(event) => setPayeeName(event.target.value)}
              />
            </div>
          ) : null}
          {direction === 'incoming' ? (
            <div className="grid gap-1.5">
              <Label htmlFor="c-issuing">{t('banking.fIssuingBank')}</Label>
              <Input
                id="c-issuing"
                value={issuingBank}
                onChange={(event) => setIssuingBank(event.target.value)}
              />
            </div>
          ) : (
            <div className="grid gap-1.5">
              <Label htmlFor="c-drawn">{t('banking.fDrawnOn')}</Label>
              <NativeSelect
                id="c-drawn"
                value={bankAccountId}
                onChange={(event) => setBankAccountId(event.target.value)}
              >
                <option value="">{t('common.choose')}</option>
                {drawable.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="c-amount">{t('banking.fAmount')}</Label>
            <Input
              id="c-amount"
              inputMode="decimal"
              dir="ltr"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="c-doc">
              {direction === 'incoming' ? t('banking.fDateReceived') : t('banking.fIssueDate')}
            </Label>
            <Input
              id="c-doc"
              type="date"
              value={docDate}
              onChange={(event) => setDocDate(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="c-date">{t('banking.fChequeDate')}</Label>
            <Input
              id="c-date"
              type="date"
              value={chequeDate}
              onChange={(event) => setChequeDate(event.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => void submit()}
            disabled={create.isPending || chequeNumber.trim() === '' || amount === ''}
          >
            {t('common.create')}
          </Button>
          <Button variant="outline" onClick={onDone}>
            {t('common.cancel')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CheckDetail({ check }: { check: CheckResponse }) {
  const { t } = useTranslation();
  const baseCurrency = useBaseCurrency();
  const transition = useTransitionCheck();
  const reverse = useReverseCheck();
  const reissue = useReissueCheck();
  const voidCheck = useVoidCheck();
  const setFlag = useSetBouncedFlag();
  const { data: bankAccounts } = useBankAccounts();
  const { data: flagHistory } = useBouncedFlagHistory(
    check.partnerBouncedCheque !== null ? check.partnerId : null,
  );

  const [depositTarget, setDepositTarget] = useState('');
  const [bounceReason, setBounceReason] = useState('insufficient_funds');
  const [bounceFee, setBounceFee] = useState('');
  const [voidReason, setVoidReason] = useState('');
  // ERR-FIN-10-07 / A12: the reason box the prototype lacked — void's mirror.
  const [returnReason, setReturnReason] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [flagOpen, setFlagOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fail = (cause: unknown) =>
    setError(localizeServerCause(t, cause, 'banking.errSaveFailedCheques'));

  // BR-FIN-10-07: deposit targets — every account except a credit card.
  const depositable = (bankAccounts ?? []).filter(
    (account) => account.accountType !== 'credit_card',
  );

  const move = (to: string) => {
    setError(null);
    setNotice(null);
    transition
      .mutateAsync({
        checkId: check.id,
        body: {
          to: to as never,
          ...(to === 'deposited' && depositTarget !== '' ? { bankAccountId: depositTarget } : {}),
          ...(to === 'bounced'
            ? {
                bounceReason: bounceReason as never,
                ...(bounceFee === '' ? {} : { bounceFee }),
              }
            : {}),
          // A12: the typed reason travels WITH the transition, so the server
          // can demand it (ERR-FIN-10-07's box, wired end to end).
          ...(to === 'returned' && returnReason.trim() !== ''
            ? { returnReason: returnReason.trim() }
            : {}),
        },
      })
      .then(() => {
        if (to === 'returned') setNotice(t('banking.returnedNotice'));
      })
      .catch(fail);
  };

  const nexts = nextStatesOf(check.status);

  return (
    <Card className="self-start">
      <CardContent className="grid gap-4">
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p role="status" className="rounded-md border border-green-300 bg-green-50 p-2 text-sm dark:border-green-900 dark:bg-green-950">
            {notice}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <Ltr value={check.code} className="font-mono text-sm text-muted-foreground" />
          <span className="me-auto text-lg font-bold">
            {check.partnerName ?? check.payeeName ?? '—'}
          </span>
          <span className="rounded-full bg-muted px-3 py-1 text-sm font-medium" data-testid="check-status">
            {t(`banking.status.${check.status}`)}
          </span>
          {/* D31, said twice on purpose: the chip is scannable on the register
              and the note below explains it here, where somebody has stopped to
              read. Same amber, same NFR-14 meaning — waiting. */}
          {check.postingDueOn !== null ? (
            <span
              data-testid="check-posting-held"
              className="rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300"
            >
              {t('banking.postingHeld', { date: check.postingDueOn })}
            </span>
          ) : null}
        </div>

        {check.postingDueOn !== null ? (
          <p role="status" className="text-sm text-muted-foreground">
            {t('banking.postingHeldNote')}
          </p>
        ) : null}

        {/*
         * BR-SAL-04-09, satisfied by the component that satisfies it for the
         * nine document tables — "the exchange rate must be snapshotted on the
         * [document's] date, AND THE 'AS OF' DATE MUST BE SHOWN". Reusing
         * `FxPanel` rather than copying it is the same argument its own header
         * makes about the nine: a tenth hand-written panel is a tenth chance
         * for one screen to quietly stop citing its date.
         *
         * `baseTotal` and `rateWarning` are null because a cheque's response
         * carries neither, and the panel already draws nothing for a null in
         * either — a cheque is one amount, not a document with a grand total,
         * and BR-MD-11-14's stale-rate warning belongs to a CONFIRMING click
         * that a cheque register does not have.
         */}
        <FxPanel
          doc={{
            currency: check.currency,
            exchangeRate: check.exchangeRate,
            rateEffectiveOn: check.rateEffectiveOn,
            baseCurrency,
            baseTotal: null,
            rateWarning: null,
          }}
        />

        {/* 5a: the customer's credit-risk flag — a property of the CUSTOMER. */}
        {check.direction === 'incoming' && check.partnerBouncedCheque !== null ? (
          <div className="grid gap-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {check.partnerBouncedCheque ? (
                <span
                  data-testid="bounce-flag"
                  className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-950 dark:text-red-300"
                >
                  <Flag aria-hidden className="size-3" /> {t('banking.flagBounceHistory')}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">{t('banking.flagClear')}</span>
              )}
              {/* Ruling R10's named extension — a grant no shipped role holds,
                  so greying it is exactly what teaches somebody it exists. */}
              <Gated
                permission="banking.override_bounced_flag"
                reasonKey="perm.cannot.overrideBouncedFlag"
              >
                <Button variant="outline" size="sm" onClick={() => setFlagOpen((value) => !value)}>
                  {t('banking.flagEdit')}
                </Button>
              </Gated>
            </div>
            {flagOpen ? (
              <div className="flex flex-wrap items-end gap-2 rounded-md border p-2">
                <div className="grid grow gap-1.5">
                  <Label htmlFor="flag-reason">{t('banking.flagReason')}</Label>
                  <Input
                    id="flag-reason"
                    value={flagReason}
                    onChange={(event) => setFlagReason(event.target.value)}
                  />
                  {/* ERR-MD-01-10's sentence, said beside its field (GEN-152)
                      while the button below stays inert — the ERR-FIN-11-02
                      shape: a greyed control explained in words. Clearing
                      only; MD-01 §14 words no sentence for a hand-raise. */}
                  {check.partnerBouncedCheque && flagReason.trim() === '' ? (
                    <p className="text-xs text-muted-foreground">
                      {localizeServerMessage(t, BANKING_MESSAGES.bouncedFlagClearReasonRequired)}
                    </p>
                  ) : null}
                </div>
                <Button
                  size="sm"
                  data-testid="flag-save"
                  disabled={flagReason.trim() === '' || setFlag.isPending}
                  onClick={() => {
                    setError(null);
                    setFlag
                      .mutateAsync({
                        partnerId: check.partnerId!,
                        body: { flagged: !check.partnerBouncedCheque, reason: flagReason.trim() },
                      })
                      .then(() => {
                        setFlagOpen(false);
                        setFlagReason('');
                      })
                      .catch(fail);
                  }}
                >
                  {check.partnerBouncedCheque ? t('banking.flagClearAction') : t('banking.flagRaiseAction')}
                </Button>
              </div>
            ) : null}
            {/* BR-FIN-10-17: the history, read beside the flag. */}
            {(flagHistory ?? []).length > 0 ? (
              <ul className="grid gap-1 text-xs text-muted-foreground">
                {(flagHistory ?? []).slice(0, 4).map((row, index) => (
                  <li key={index}>
                    <Ltr value={row.occurredAt.slice(0, 10)} className="font-mono" /> ·{' '}
                    {row.actorType === 'system' ? t('banking.flagBySystem') : (row.actorEmail ?? '')}{' '}
                    · {row.after ? t('banking.flagRaised') : t('banking.flagCleared')}
                    {row.reason ? ` — ${row.reason}` : ''}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="grid gap-3 text-sm sm:grid-cols-3">
          <div>
            <p className="text-xs text-muted-foreground">{t('banking.fChequeNumber')}</p>
            <Ltr value={check.chequeNumber} className="font-mono" />
            {check.numberOutOfRange ? (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle aria-hidden className="size-3" /> {t('banking.numberOutOfRange')}
              </p>
            ) : null}
          </div>
          <div>
            {/* BR-FIN-10-02: display-only — written for what it is written for. */}
            <p className="text-xs text-muted-foreground">{t('banking.fAmount')}</p>
            <span className="font-bold">
              <Amount value={check.amount} currency={check.currency} />
            </span>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">
              {check.direction === 'incoming' ? t('banking.fDateReceived') : t('banking.fIssueDate')}
            </p>
            <Ltr value={check.docDate} className="font-mono" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t('banking.fChequeDate')}</p>
            <Ltr value={check.chequeDate} className="font-mono" />
          </div>
          {check.direction === 'incoming' ? (
            <div>
              <p className="text-xs text-muted-foreground">{t('banking.fIssuingBank')}</p>
              {check.issuingBank ?? '—'}
            </div>
          ) : (
            <div>
              <p className="text-xs text-muted-foreground">{t('banking.fDrawnOn')}</p>
              {check.bankAccountName ?? '—'}
            </div>
          )}
          {check.depositDate !== null ? (
            <div>
              <p className="text-xs text-muted-foreground">{t('banking.fDepositDate')}</p>
              <Ltr value={check.depositDate} className="font-mono" />
            </div>
          ) : null}
          {check.direction === 'incoming' && check.bankAccountName !== null ? (
            <div>
              <p className="text-xs text-muted-foreground">{t('banking.fDepositedInto')}</p>
              {check.bankAccountName}
            </div>
          ) : null}
          {check.presentedDate !== null ? (
            <div>
              <p className="text-xs text-muted-foreground">{t('banking.fPresentedDate')}</p>
              <Ltr value={check.presentedDate} className="font-mono" />
            </div>
          ) : null}
          {check.clearanceDate !== null ? (
            <div>
              <p className="text-xs text-muted-foreground">{t('banking.fClearanceDate')}</p>
              <Ltr value={check.clearanceDate} className="font-mono text-green-700 dark:text-green-400" />
            </div>
          ) : null}
        </div>

        {/* The lifecycle: only legal next states are offered. */}
        {/* WHICH next states exist is the cheque's lifecycle; whether the
            move is usable is the role's, and it greys per button below. */}
        {nexts.length > 0 ? (
          <div className="grid gap-3 rounded-md border p-3">
            <p className="text-sm font-medium">{t('banking.lifecycle')}</p>
            {nexts.includes('deposited') ? (
              <div className="grid gap-1.5">
                <Label htmlFor="deposit-target">{t('banking.fDepositedInto')}</Label>
                <NativeSelect
                  id="deposit-target"
                  value={depositTarget}
                  onChange={(event) => setDepositTarget(event.target.value)}
                >
                  <option value="">{t('common.choose')}</option>
                  {depositable.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            ) : null}
            {/* ERR-FIN-10-07 / A12: the reason box, drawn beside the move
                that needs it — the void panel's shape on the lifecycle row.
                The Return button below is inert until a reason is typed, and
                the server refuses a bare `returned` independently. */}
            {nexts.includes('returned') ? (
              <div className="grid gap-1.5">
                <Label htmlFor="return-reason">{t('banking.fReturnReason')}</Label>
                <Textarea
                  id="return-reason"
                  data-testid="return-reason"
                  rows={2}
                  placeholder={t('banking.returnPlaceholder')}
                  value={returnReason}
                  onChange={(event) => setReturnReason(event.target.value)}
                />
              </div>
            ) : null}
            {nexts.includes('bounced') ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="bounce-reason">{t('banking.fBounceReason')}</Label>
                  <NativeSelect
                    id="bounce-reason"
                    value={bounceReason}
                    onChange={(event) => setBounceReason(event.target.value)}
                  >
                    {(['insufficient_funds', 'signature_mismatch', 'account_closed', 'other'] as const).map(
                      (reason) => (
                        <option key={reason} value={reason}>
                          {t(`banking.bounceReason.${reason}`)}
                        </option>
                      ),
                    )}
                  </NativeSelect>
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="bounce-fee">{t('banking.fBounceFee')}</Label>
                  <Input
                    id="bounce-fee"
                    inputMode="decimal"
                    dir="ltr"
                    value={bounceFee}
                    onChange={(event) => setBounceFee(event.target.value)}
                  />
                </div>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {nexts.map((next) => (
                <Gated
                  key={next}
                  permission="banking.approve"
                  reasonKey="perm.cannot.moveChecks"
                >
                <Button
                  variant={next === 'bounced' ? 'destructive' : 'default'}
                  data-testid={`move-${next}`}
                  onClick={() => move(next)}
                  // A12, mirroring the void button: Return to customer is
                  // inert until its reason is typed (BR-FIN-11-04's shape).
                  disabled={
                    transition.isPending || (next === 'returned' && returnReason.trim() === '')
                  }
                  className={next === 'returned' && returnReason.trim() === '' ? 'opacity-50' : ''}
                >
                  {t(`banking.moveTo.${next}`)}
                </Button>
                </Gated>
              ))}
            </div>
          </div>
        ) : null}

        {/* The bounce aftermath: one-shot Reverse; outgoing also Reissue. */}
        {check.status === 'bounced' ? (
          <div className="grid gap-2 rounded-md border border-red-300 p-3 dark:border-red-900">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              {t('banking.bouncePanel')}
            </p>
            {check.reversedAt !== null ? (
              <p className="text-sm text-muted-foreground" data-testid="reversed-note">
                {check.direction === 'incoming'
                  ? t('banking.reversedNoteIn')
                  : t('banking.reversedNoteOut')}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Gated permission="banking.approve" reasonKey="perm.cannot.reverseChecks">
                <Button
                  variant="destructive"
                  data-testid="reverse-check"
                  disabled={!canReverse(check) || reverse.isPending}
                  className={check.reversedAt !== null ? 'opacity-50' : ''}
                  onClick={() => {
                    setError(null);
                    reverse
                      .mutateAsync(check.id)
                      .then((result) => {
                        if ('message' in result) setNotice(t('banking.reversedToast'));
                      })
                      .catch(fail);
                  }}
                >
                  <Undo2 aria-hidden /> {t('banking.reverseCheck')}
                </Button>
              </Gated>
              {canReissue(check) ? (
                <Button
                  variant="outline"
                  disabled={reissue.isPending}
                  onClick={() => {
                    setError(null);
                    reissue.mutateAsync(check.id).catch(fail);
                  }}
                >
                  {t('banking.reissueCheck')}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* FIN-11: the void panel — ONLY while Issued, reason mandatory. */}
        {/* `canVoid` is the cheque's own state — Issued only — and the void
            REASON is mandatory for everybody. The permission greys the button
            inside the panel rather than removing the panel, so a clerk can see
            that voiding is possible and ask for it. */}
        {canVoid(check) ? (
          <div className="grid gap-2 rounded-md border p-3">
            <p className="text-sm font-medium">{t('banking.voidPanel')}</p>
            <p className="text-xs text-muted-foreground">{t('banking.voidHint')}</p>
            <div className="grid gap-1.5">
              <Label htmlFor="void-reason">{t('banking.fVoidReason')}</Label>
              <Textarea
                id="void-reason"
                rows={2}
                placeholder={t('banking.voidPlaceholder')}
                value={voidReason}
                onChange={(event) => setVoidReason(event.target.value)}
              />
            </div>
            <div>
              <Gated permission="banking.delete" reasonKey="perm.cannot.voidChecks">
              <Button
                variant="destructive"
                data-testid="void-check"
                // BR-FIN-11-04: inert until a reason is typed.
                disabled={voidReason.trim() === '' || voidCheck.isPending}
                className={voidReason.trim() === '' ? 'opacity-50' : ''}
                onClick={() => {
                  setError(null);
                  voidCheck
                    .mutateAsync({ checkId: check.id, reason: voidReason.trim() })
                    .then(() => setNotice(t('banking.voidedNotice')))
                    .catch(fail);
                }}
              >
                {t('banking.voidCheck')}
              </Button>
              </Gated>
            </div>
          </div>
        ) : null}
        {check.status === 'voided' ? (
          <p className="rounded-md border p-2 text-sm text-muted-foreground">
            {t('banking.voidedNotice')}
            {check.voidReason ? ` — ${check.voidReason}` : ''}
          </p>
        ) : null}
        {/* A12's notice, mirroring the voided one: the reason stays readable
            on the returned cheque. A return that predates the ruling has no
            reason to show, and the notice stands alone. */}
        {check.status === 'returned' ? (
          <p className="rounded-md border p-2 text-sm text-muted-foreground" data-testid="returned-note">
            {t('banking.returnedNotice')}
            {check.returnReason ? ` — ${check.returnReason}` : ''}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
